import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  HostBinding,
  inject,
  input,
} from '@angular/core';
import { Nullish, Option, isString } from '@fmnts/core';
import { formatISO, isValidDate, parseISO } from '@fmnts/core/chronos';
import {
  AbstractDeclarativeInputFieldComponent,
  FormInputField,
} from '@fmnts/declarative-forms';
import { SharedDeclarativeCampaignFormsModule } from '../../shared-declarative-next-forms.module';
import { DateFormatDirective } from './date-format.directive';

function dateFromIsoString(value: string | Nullish) {
  if (!isString(value)) {
    return null;
  }

  const asDate = parseISO(value);
  return isValidDate(asDate) ? asDate : null;
}

type ControlValue = Option<Date>;

@Component({
  selector: 'next-date-field',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './date-field.component.html',
  standalone: true,
  imports: [SharedDeclarativeCampaignFormsModule, DateFormatDirective],
})
export class DateFieldComponent
  extends AbstractDeclarativeInputFieldComponent<FormInputField, ControlValue>
  implements DoCheck
{
  private readonly cd = inject(ChangeDetectorRef);

  @HostBinding('class') componentClass = 'form-field form-field__date';

  /**
   * Value for the `autocomplete` attribute of the input field.
   */
  readonly autocomplete = input(this._getAutocompleteBehavior());

  /** Used to perform change detection checks */
  private _wasTouched = false;

  private _value: ControlValue = null;

  /**
   * Date as ISO 8601 string or empty string if value is null.
   * This is to match the native `<input type="date">` behavior.
   */
  get isoDate(): string {
    return this._value
      ? formatISO(this._value, { representation: 'date' })
      : '';
  }

  set isoDate(value: string | null) {
    this._changeValue(dateFromIsoString(value));
  }

  /**
   * represents the internal date data structure that's either null or Moment
   */
  get value(): ControlValue {
    return this._value;
  }

  set value(value: ControlValue) {
    this._changeValue(value);
    this._onTouched();
  }

  ngDoCheck(): void {
    // Certain values are needed to perform checks
    const { control, field, form } = this;
    if (!form || !field || !control) {
      return;
    }

    // Perform change detection checks
    // Check if `touched` changed
    if (control.touched !== this._wasTouched) {
      this._wasTouched = control.touched;
      this.cd.markForCheck();
    }
  }

  writeValue(value: string | Date | Nullish): void {
    // If a string was passed, this is most likely due to some
    // default value or restored value. In that case, set the value
    // of the control directly, which will call `writeValue` again.
    if (isString(value)) {
      this._updateControlValue(dateFromIsoString(value));
      // Nothing more to do, bc the update will call writeValue again
      return;
    }

    this._value = value;
    this.cd.markForCheck();
  }

  get showError(): boolean {
    return this.form && this.control.touched && this.control.invalid;
  }

  /**
   * Changes the value and calls the onChange listeners.
   *
   * @param value The new value
   */
  private _changeValue(value: ControlValue): void {
    this._value = value;
    this._onChange(value);
  }

  /**
   * Patches the value in the control
   * without changing the pristine/touched/dirty states.
   *
   * @param newValue The new value
   */
  private _updateControlValue(
    value: ControlValue,
    options: Partial<{ emitModelToViewChange: boolean }> = {},
  ): void {
    const { control } = this;
    // Use setValue to keep the field pristine
    // and to emit the value change to listeners
    control.setValue(value, { ...options });
  }
}
